// Lógica pura del flujo de actas de asamblea en el Editor (Fase 2): estados,
// plazo de comunicación, número de acta, resultados que se congelan al
// publicar, padrón congelado, marcas de revisión, cambios de una minuta y
// tipos de audio. Sin React ni Firebase para poder testearla aislada.

import { defaultActa, emptyPunto, emptyVotacion, reconciliarAsistencia, pad2 } from "../../utils/actaConfig";
import { calcularQuorum, calcularVotacion, validarVotacion } from "../../utils/actaVoting";
import { esUnidadDelEdificio } from "../../utils/unidadesEdificio";

// ───────────────────────── Estados ─────────────────────────

// Los badges reusan claves existentes de statusColors.js (con label propio)
// para no inventar colores.
export const ESTADOS_ACTA = {
  borrador: { label: "Borrador", badge: "borrador" },
  en_revision: { label: "En revisión", badge: "en_proceso" },
  publicada: { label: "Publicada", badge: "aprobada" },
};

export function estadoActa(status) {
  return ESTADOS_ACTA[status] || ESTADOS_ACTA.borrador;
}

export const ESTADOS_PROCESAMIENTO = {
  sin_audio: { label: "Sin audio", badge: "libre" },
  pendiente: { label: "En cola", badge: "pendiente" },
  procesando: { label: "Procesando", badge: "en_proceso" },
  listo: { label: "Listo", badge: "completada" },
  error: { label: "Con error", badge: "error" },
  // El staff lo sacó de la cola (cancelarProcesamiento): se puede volver a procesar.
  cancelado: { label: "Cancelado", badge: "anulada" },
};

// Etapas del worker con audio subido (fuente "subida").
export const ETAPAS_PROCESAMIENTO = [
  { id: "preparar_audio", label: "Preparar el audio" },
  { id: "stt", label: "Transcribir" },
  { id: "generar_acta", label: "Redactar el acta" },
];

// Con la grabación de Zoom (fuente "zoom"): antes, buscar la reunión (sesiones
// y asistencia) y traer la grabación; después del STT, ponerle nombre a las
// voces con las pistas de cada participante.
export const ETAPAS_ZOOM = [
  { id: "conciliar_zoom", label: "Buscar la reunión de Zoom" },
  { id: "importar_zoom", label: "Traer la grabación" },
  { id: "preparar_audio", label: "Preparar el audio" },
  { id: "stt", label: "Transcribir" },
  { id: "hablantes", label: "Identificar voces" },
  { id: "generar_acta", label: "Redactar el acta" },
];

const ETAPAS_SOLO_ZOOM = new Set(["conciliar_zoom", "importar_zoom", "hablantes"]);

/** Etapas que corre el worker: las de Zoom si la fuente es Zoom (o si ya va por una etapa de Zoom). */
export function etapasDe(p) {
  return p && (p.fuente === "zoom" || ETAPAS_SOLO_ZOOM.has(p.etapa)) ? ETAPAS_ZOOM : ETAPAS_PROCESAMIENTO;
}

export const procesamientoEnCurso = (p) => !!p && (p.estado === "pendiente" || p.estado === "procesando");

const esObj = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const booleano = (v) => (typeof v === "boolean" ? v : null);
const FUENTES = new Set(["subida", "zoom"]);

/**
 * Minutas activas de la asamblea que manda estadoProcesamiento
 * (`minutas`: [{ minutaId, actaId, estado, origen, creadaEnMs, solicitadaEnMs, lanzamiento }]).
 */
function normalizarMinutas(lista) {
  return (Array.isArray(lista) ? lista : [])
    .filter((m) => esObj(m) && m.minutaId != null && m.minutaId !== "")
    .map((m) => ({
      minutaId: String(m.minutaId),
      actaId: m.actaId != null && m.actaId !== "" ? String(m.actaId) : null,
      estado: m.estado || "pendiente",
      // "texto": escrita en el editor; sin el dato (servidor viejo), de audio.
      origen: m.origen === "texto" ? "texto" : "audio",
      creadaEnMs: Number(m.creadaEnMs) || null,
      solicitadaEnMs: Number(m.solicitadaEnMs) || null,
      lanzamiento: esObj(m.lanzamiento) ? m.lanzamiento : null,
      workerHabilitado: booleano(m.workerHabilitado),
    }));
}

/**
 * Respuesta de estadoProcesamiento → procesamiento con defaults. Acepta
 * { procesamiento: {...}, workerHabilitado } o el objeto suelto; sin doc
 * todavía = "sin_audio". `workerHabilitado` (true/false, o null si el
 * servidor no lo dijo) viene en el nivel de arriba de la respuesta. Fase 3:
 * `fuente` ("subida" | "zoom" | null), `zoom` (resumen de la importación, sin
 * links) y `minutas` (las activas de la asamblea, para cancelarlas).
 */
export function normalizarProcesamiento(res) {
  const p = esObj(res) && "procesamiento" in res ? res.procesamiento : res;
  const workerHabilitado = booleano(esObj(res) ? res.workerHabilitado : null) ?? booleano(esObj(p) ? p.workerHabilitado : null);
  const minutas = normalizarMinutas(esObj(res) && Array.isArray(res.minutas) ? res.minutas : esObj(p) ? p.minutas : null);
  const base = { estado: "sin_audio", etapa: null, audios: [], intentos: 0, ultimoError: null, actaId: null, lanzamiento: null, fuente: null, zoom: null };
  if (!esObj(p)) return { ...base, minutas, workerHabilitado };
  return {
    ...base,
    ...p,
    estado: p.estado || "sin_audio",
    audios: Array.isArray(p.audios) ? p.audios : [],
    fuente: FUENTES.has(p.fuente) ? p.fuente : null,
    zoom: esObj(p.zoom) ? p.zoom : null,
    minutas,
    workerHabilitado,
  };
}

/**
 * Grabaciones de Zoom sin importar (0 si no hay): la fuente es Zoom, no está
 * en cola ni corriendo y Zoom avisó grabaciones que el worker no trajo (el
 * webhook no la pudo encolar porque ya había un acta, la importación falló,
 * o la reunión se reinició después de importar). Lo dice el servidor en
 * `zoom.grabacionPendiente` ({ sesiones, ultimoAvisoEnMs } o null). Sin ese
 * dato (servidor viejo): pendiente si no se importó nada y no está listo.
 */
export function grabacionesZoomSinImportar(p) {
  if (!p || p.fuente !== "zoom" || procesamientoEnCurso(p)) return 0;
  const z = esObj(p.zoom) ? p.zoom : {};
  if ("grabacionPendiente" in z) {
    const g = z.grabacionPendiente;
    if (esObj(g)) return Math.max(1, Math.floor(Number(g.sesiones)) || 1);
    return g === true ? 1 : 0;
  }
  return !Number(z.importadoEnMs) && p.estado !== "listo" ? 1 : 0;
}

/** ¿Hay alguna grabación de Zoom sin importar? ("Hay una grabación de Zoom sin importar": Procesar la trae) */
export const grabacionZoomSinImportar = (p) => grabacionesZoomSinImportar(p) > 0;

const msDe = (v) => {
  if (typeof v === "number" && Number.isFinite(v) && v > 0) return v;
  if (typeof v === "string" && v) {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : t;
  }
  return null;
};

/**
 * Sesiones de Zoom que empezaron fuera del horario de la asamblea (después del
 * fin previsto, o mucho después de que terminó la primera): el worker no las
 * importa solas. Están en `zoom.instancias` con `requiereConfirmacion` hasta
 * que el staff las incluye (incluirSesionZoom las marca con confirmadaEnMs).
 * → [{ uuid, sesion, inicioMs, finMs, duracionMin, sinGrabacion }]
 */
export function sesionesZoomPorConfirmar(p) {
  const z = p && esObj(p.zoom) ? p.zoom : null;
  if (!z || !Array.isArray(z.instancias)) return [];
  return z.instancias
    .filter(
      (i) =>
        esObj(i) &&
        i.requiereConfirmacion === true &&
        i.incluida !== true &&
        !(Number(i.confirmadaEnMs) > 0) &&
        !(Number(i.importadaEnMs) > 0) &&
        typeof i.uuid === "string" &&
        i.uuid
    )
    .map((i) => {
      const inicioMs = msDe(i.inicioMs ?? i.start_time ?? i.startTime);
      const finMs = msDe(i.finMs ?? i.end_time ?? i.endTime);
      const duracion = inicioMs && finMs && finMs > inicioMs ? Math.round((finMs - inicioMs) / 60000) : Number(i.duracionMin ?? i.duration) || null;
      return { uuid: i.uuid, sesion: Number(i.sesion) || null, inicioMs, finMs, duracionMin: duracion, sinGrabacion: i.sinGrabacion === true };
    })
    .sort((a, b) => (a.inicioMs || 0) - (b.inicioMs || 0));
}

/** ¿El audio lo trajo el worker desde Zoom? (asambleas/{aid}/audio/original/z{sesion}-{n}-{huella}.m4a) */
export const esAudioDeZoom = (audio) => /\/audio\/original\/z\d{1,4}-/.test(String((audio && audio.path) || ""));

/**
 * ¿Quedó en cola porque el procesamiento automático (el worker) está apagado?
 * Manda `workerHabilitado` cuando el servidor lo informa: estadoProcesamiento
 * y estadoMinuta ya miran los interruptores globales Y que el edificio esté en
 * edificiosHabilitados (en modo local, true si el módulo y el edificio están
 * habilitados). Así, cuando Habitia vuelve a prender el worker o el edificio,
 * el seguimiento normal vuelve enseguida aunque el lanzamiento siga diciendo
 * "deshabilitado" hasta que el watchdog lo relance. El lanzamiento
 * "deshabilitado" que marcó el trigger queda solo como respaldo cuando la
 * respuesta no trae el dato. No vale para lo que ya arrancó: solo para lo que
 * espera en cola.
 */
export function workerApagado(x) {
  if (!x || x.estado !== "pendiente") return false;
  if (typeof x.workerHabilitado === "boolean") return !x.workerHabilitado;
  return esObj(x.lanzamiento) && x.lanzamiento.estado === "deshabilitado";
}

export const AVISO_WORKER_APAGADO =
  "El procesamiento automático todavía no está activo: queda en cola y arranca cuando se habilite.";

/**
 * ¿Está en cola para el worker LOCAL? En modo local functions no lanza el
 * Cloud Run Job: deja lanzamiento { estado: "local" } y lo toma el demonio de
 * scripts/local. Se muestra como "En cola (local)".
 */
export const enColaLocal = (x) => !!x && x.estado === "pendiente" && esObj(x.lanzamiento) && x.lanzamiento.estado === "local";

/** Texto del estado del procesamiento para el badge ("En cola (local)" en modo local). */
export function etiquetaProcesamiento(p) {
  if (enColaLocal(p)) return "En cola (local)";
  return (ESTADOS_PROCESAMIENTO[p && p.estado] || ESTADOS_PROCESAMIENTO.sin_audio).label;
}

/**
 * ¿Se le ofrece al staff "Cancelar procesamiento"? Solo a lo que espera en
 * cola con el worker apagado: eso no va a avanzar solo y, mientras tanto, el
 * acta de la asamblea no se puede guardar ni publicar.
 */
export const puedeCancelarProcesamiento = (p) => workerApagado(p);

/**
 * Minutas que se le ofrece cancelar al staff (cancelarMinuta), con la misma
 * regla que el procesamiento: en cola con el worker apagado. El dato
 * workerHabilitado de la minuta manda; si no vino, el de la respuesta.
 */
export function minutasParaCancelar(p) {
  const lista = p && Array.isArray(p.minutas) ? p.minutas : [];
  const general = p ? booleano(p.workerHabilitado) : null;
  return lista.filter((m) => workerApagado({ ...m, workerHabilitado: booleano(m.workerHabilitado) ?? general }));
}

/** Audios ordenados como los concatena el worker: por sesión y número de parte. */
export function audiosOrdenados(audios) {
  return [...(audios || [])].sort(
    (a, b) => (Number(a.sesion) || 0) - (Number(b.sesion) || 0) || (Number(a.n) || 0) - (Number(b.n) || 0)
  );
}

export const ESTADOS_MINUTA = {
  subida: "Subida",
  pendiente: "En cola",
  procesando: "Procesando",
  aplicada: "Aplicada",
  error: "Con error",
  // El staff la sacó de la cola (cancelarMinuta): se puede volver a aplicar.
  cancelada: "Cancelada",
};

export const minutaEnCurso = (m) => !!m && ["subida", "pendiente", "procesando"].includes(m.estado);

/**
 * ¿La minuta deja el acta en solo lectura? Solo mientras el worker la puede
 * estar aplicando: si quedó en cola con el worker apagado, el acta se sigue
 * editando (la minuta se aplica cuando se habilite).
 */
export const minutaBloqueaActa = (m) => minutaEnCurso(m) && !(m.workerApagado || workerApagado(m));

// ───────────────────────── Fechas y formatos ─────────────────────────

const TZ = "America/Montevideo";
const DIA_MS = 86400000;

/** Plazo legal para comunicar el acta a los copropietarios (días corridos). */
export const PLAZO_COMUNICACION_DIAS = 30;

function isoAUtc(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || "").trim());
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(t) ? null : t;
}

const utcAIso = (t) => new Date(t).toISOString().slice(0, 10);

/** Fecha calendario en Montevideo de un instante ("YYYY-MM-DD"). */
export function fechaLocalIso(ms) {
  // en-CA formatea como YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(
    new Date(ms)
  );
}

/**
 * Cuenta de los 30 días para comunicar el acta, desde la fecha de la asamblea.
 * @returns {null | { vence: string, diasRestantes: number, vencido: boolean }}
 */
export function plazoComunicacion(fechaAsamblea, ahoraMs = Date.now()) {
  const base = isoAUtc(fechaAsamblea);
  if (base == null) return null;
  const vence = base + PLAZO_COMUNICACION_DIAS * DIA_MS;
  const hoy = isoAUtc(fechaLocalIso(ahoraMs));
  const diasRestantes = Math.round((vence - hoy) / DIA_MS);
  return { vence: utcAIso(vence), diasRestantes, vencido: diasRestantes < 0 };
}

/** Días corridos entre la asamblea y la publicación del acta (null si falta un dato). */
export function diasHastaPublicacion(fechaAsamblea, publicadaEnMs) {
  const base = isoAUtc(fechaAsamblea);
  if (base == null || !publicadaEnMs) return null;
  return Math.round((isoAUtc(fechaLocalIso(publicadaEnMs)) - base) / DIA_MS);
}

/** "2026-10-19" → "19/10/2026". */
export function fechaCorta(iso) {
  const t = isoAUtc(iso);
  if (t == null) return iso ? String(iso) : "—";
  const [y, m, d] = utcAIso(t).split("-");
  return `${d}/${m}/${y}`;
}

/**
 * Instante → "19/10/2026, 18:30" en hora de Montevideo. Siempre 24 h: con
 * es-UY el ICU de los navegadores (y de Node) da "06:30 p. m." si no se pide.
 */
export function fechaHora(ms) {
  if (!ms) return "—";
  return new Date(Number(ms)).toLocaleString("es-UY", {
    timeZone: TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}

/** Milisegundos de audio → "m:ss" o "h:mm:ss". */
export function formatoTiempo(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

/** Bytes → "850 KB", "230 MB", "1,2 GB". */
export function formatoBytes(bytes) {
  const b = Number(bytes) || 0;
  const fmt = (n, dec) => n.toLocaleString("es-UY", { maximumFractionDigits: dec });
  if (b >= 1024 ** 3) return `${fmt(b / 1024 ** 3, 1)} GB`;
  if (b >= 1024 ** 2) return `${fmt(b / 1024 ** 2, b >= 100 * 1024 ** 2 ? 0 : 1)} MB`;
  return `${fmt(Math.max(1, Math.round(b / 1024)), 0)} KB`;
}

// ───────────────────────── Número de acta ─────────────────────────

/**
 * Número previsto para mostrar antes de publicar cuando el servidor no lo
 * informó: el correlativo del edificio desde el mayor número de las actas
 * PUBLICADAS (formato "001"), la misma regla del servidor. Es solo una
 * estimación: el que se imprime en el PDF es el que devuelve pdfSubida (el
 * servidor también sigue el contador del edificio si se sembró con el libro
 * de actas en papel) y publicar lo vuelve a verificar.
 */
export function proximoNumeroActa(actas) {
  let max = 0;
  let ancho = 3;
  for (const a of actas || []) {
    if (!a || a.status !== "publicada") continue;
    const n = String(a.actaNumero ?? "").trim();
    if (!/^\d{1,9}$/.test(n)) continue;
    max = Math.max(max, Number(n));
    ancho = Math.max(ancho, n.length);
  }
  return String(max + 1).padStart(ancho, "0");
}

// ───────────────────────── Quórum y resultados ─────────────────────────

/** Umbral de quórum habilitante: 1ª convocatoria 50 %; en 2ª se sesiona con los presentes. */
export function umbralQuorum(convocatoria) {
  return convocatoria === "primera" ? 50 : 0;
}

/**
 * Resultados que se congelan al publicar (resultadosSnapshot del contrato):
 * quórum + cómputo de cada punto con votación, con el mismo motor que el PDF.
 */
export function calcularResultadosSnapshot(acta, edificio) {
  const umbral = umbralQuorum(acta && acta.convocatoria);
  const quorum = { ...calcularQuorum(acta, edificio, umbral), umbral };
  const votaciones = {};
  for (const p of (acta && acta.ordenDelDia) || []) {
    if (!p || !p.id || p.tipoResolucion === "informativa") continue;
    const tipoResolucion = p.tipoResolucion || "mayoria";
    votaciones[p.id] = { ...calcularVotacion(p.votacion, acta, edificio, tipoResolucion), tipoResolucion };
  }
  return { quorum, votaciones };
}

// ───────────────────────── Padrón ─────────────────────────
//
// El padrón vivo (con el que se arma el PDF y los resultados antes de
// publicar) y el que congela el servidor al publicar (padronSnapshot, de
// functions/asambleas/lib/padron.js armarPadron + congelarPadron) siguen las
// MISMAS reglas: sin portería, etiqueta "Apto 101", titular = owner (o el
// secundario si no hay), unidades por etiqueta y copropietarios por nombre.
// Así el acta publicada se ve igual que el PDF que se imprimió.

/** Id como lo normaliza el servidor (normId de functions/lib/access.js): 11, "11" y "011" → "11". */
export function normId(v) {
  if (v == null || typeof v === "boolean" || typeof v === "object") return null;
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : null;
  const s = String(v).trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return Number.isSafeInteger(n) ? String(n) : s;
  }
  return s;
}

/**
 * Las unidades del edificio (portería y "Alquiler del edificio") no son de un
 * copropietario: no entran al padrón (igual que en el servidor, el worker y Habi).
 */
export const fueraDelPadron = (apt) => esUnidadDelEdificio(apt);

/** Etiqueta de una unidad, igual que etiquetaUnidad del servidor ("Apto 502", "Local 2"). */
export function etiquetaUnidad(apt) {
  const a = apt || {};
  const n = a.number ?? a.numero ?? a.apartmentNumber ?? a.unidad ?? a.name;
  if (n == null || String(n).trim() === "") return `Unidad ${String(a.id).slice(0, 6)}`;
  const s = String(n).trim();
  return /^\d+[A-Za-z]?$/.test(s) ? `Apto ${s}` : s;
}

/** "101" → "Unidad 101"; "Apto 101" o "Local 2" quedan como están. */
export const nombreUnidad = (etiqueta) => {
  const e = String(etiqueta ?? "").trim();
  return /^\d/.test(e) || !e ? `Unidad ${e || "?"}` : e;
};

/**
 * Nombre de una persona como lo congela el servidor (armarPadron): nombre y
 * apellidos o, si no hay, `name` tal cual. El editor le pasa los docs crudos
 * de users (los mismos que lee el servidor), así que no hay sentinelas como
 * el "Sin nombre" de fetchUsersByUserIds que filtrar.
 */
function nombreDeUsuario(u) {
  if (!u) return "";
  const completo = [u.NombreCompleto, u.ApellidosCompleto].filter(Boolean).join(" ").trim();
  return completo || (u.name ? String(u.name) : "");
}

/**
 * Padrón vivo del edificio para el editor ({ edificio, copropietarios }, la
 * forma de buildPadron) con las reglas del padrón congelado (ver arriba).
 * @param {object} building    edificio seleccionado ({ buildingid, name, padron?, rut? })
 * @param {object[]} apartments docs de apartments del edificio, con id
 * @param {object[]} users      docs CRUDOS de users de los titulares (con userid), sin
 *                              deduplicar: un doc por typeuser y las fichas archivadas
 */
export function armarPadronVivo(building, apartments, users) {
  // Si hay varios docs con el mismo userid, el de residente gana (como en el servidor).
  const usersById = new Map();
  for (const u of users || []) {
    const id = normId(u && u.userid);
    if (!id || u.archivedAt || u.isDemo === true) continue;
    const prev = usersById.get(id);
    if (!prev || String(u.typeuser ?? "0") === "0") usersById.set(id, u);
  }
  const copro = new Map();
  const agregar = (id) => {
    if (copro.has(id)) return;
    const u = usersById.get(id);
    const nombre = nombreDeUsuario(u);
    copro.set(id, { id, nombre: nombre || `ID ${id}`, orden: nombre, ci: (u && (u.ci || u.documento)) || "", email: (u && u.email) || "" });
  };
  const unidades = [];
  for (const apt of apartments || []) {
    if (!apt || fueraDelPadron(apt)) continue;
    const titulares = [];
    for (const campo of ["owner", "ownerSecondary"]) {
      const id = normId(apt[campo]);
      if (id && !titulares.includes(id)) titulares.push(id);
    }
    titulares.forEach(agregar);
    const alicuota =
      apt.alicuota != null && apt.alicuota !== "" && !Number.isNaN(Number(apt.alicuota)) ? Number(apt.alicuota) : undefined;
    unidades.push({ id: String(apt.id), etiqueta: etiquetaUnidad(apt), alicuota, titularId: titulares[0] || "", m2: apt.m2 || "" });
  }
  unidades.sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, "es", { numeric: true }));
  const copropietarios = [...copro.values()]
    .sort((a, b) => a.orden.localeCompare(b.orden, "es"))
    .map(({ orden, ...c }) => c);
  return {
    edificio: {
      id: String((building && building.buildingid) ?? ""),
      nombre: (building && building.name) || "",
      padron: (building && (building.padron || building.rut)) || "",
      unidades,
    },
    copropietarios,
  };
}

/**
 * Padrón congelado al publicar → la forma { edificio, copropietarios } que
 * usan ActaTemplate y ActaForm. La C.I. no se congela: se toma del padrón
 * vivo (si la persona sigue en él) solo para la vista.
 */
export function padronDesdeSnapshot(snapshot, base = {}, vivo = null) {
  if (!snapshot || !Array.isArray(snapshot.unidades)) return null;
  const vivos = new Map(((vivo && vivo.copropietarios) || []).map((c) => [String(c.id), c]));
  const copro = new Map();
  const agregar = (id, nombre) => {
    const v = vivos.get(id);
    copro.set(id, { id, nombre: nombre || (v && v.nombre) || `ID ${id}`, ci: (v && v.ci) || "", email: "" });
  };
  for (const c of snapshot.copropietarios || []) {
    if (c && c.id != null && c.id !== "") agregar(String(c.id), c.nombre);
  }
  const unidades = snapshot.unidades.map((u) => {
    const titularId = u.titularId != null && u.titularId !== "" ? String(u.titularId) : "";
    if (titularId && !copro.has(titularId)) agregar(titularId, u.titularNombre);
    const alicuota =
      u.alicuota != null && u.alicuota !== "" && !Number.isNaN(Number(u.alicuota)) ? Number(u.alicuota) : undefined;
    return { id: String(u.id), etiqueta: String(u.etiqueta ?? u.id), alicuota, titularId, m2: "" };
  });
  return {
    edificio: { id: String(base.id ?? ""), nombre: base.nombre || "", padron: base.padron || "", unidades },
    copropietarios: [...copro.values()],
  };
}

// ───────────────────────── Acta en el editor ─────────────────────────

/**
 * ¿El edificio puede publicar actas (módulo Asambleas habilitado)? Lo dicen
 * detalle y listar; null = el servidor no lo informó.
 */
export function puedePublicarDe(res) {
  if (!esObj(res)) return null;
  return booleano(res.puedePublicar) ?? booleano(esObj(res.acta) ? res.acta.puedePublicar : null);
}

/** Revisiones del acta que devuelve detalle (las últimas), con tipos limpios; null si no vinieron. */
export function normalizarRevisiones(lista) {
  if (!Array.isArray(lista)) return null;
  const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
  return lista
    .filter((r) => esObj(r) && r.id != null && r.id !== "")
    .map((r) => ({
      id: String(r.id),
      tipo: r.tipo || null,
      enMs: num(r.enMs),
      versionAntes: num(r.versionAntes),
      versionDespues: num(r.versionDespues),
      minutaId: r.minutaId ? String(r.minutaId) : null,
      deshecha: !!(r.deshecha || r.deshechaEnMs),
    }));
}

/**
 * La minuta aplicada que todavía se puede deshacer: una revisión tipo
 * "minuta", no deshecha, que dejó el acta en la versión actual (nadie la
 * editó después). Así "Deshacer" sobrevive a recargar la página.
 * @returns {null | { revisionId, minutaId, enMs }}
 */
export function minutaParaDeshacer(revisiones, version) {
  const v = Number(version);
  if (!Array.isArray(revisiones) || !Number.isFinite(v)) return null;
  const r = revisiones
    .filter((x) => x && x.tipo === "minuta" && !x.deshecha && x.minutaId && Number(x.versionDespues) === v)
    .sort((a, b) => (b.enMs || 0) - (a.enMs || 0))[0];
  return r ? { revisionId: r.id, minutaId: r.minutaId, enMs: r.enMs || null } : null;
}

/**
 * Respuesta de actaAdmin "detalle" → { meta, data, revision, ia, revisiones,
 * puedePublicar }. Tolera las actas manuales viejas sin status ni version
 * (previas al backfill).
 */
export function normalizarDetalle(res, actaId = null) {
  const a = (res && res.acta) || {};
  const data = a.data && typeof a.data === "object" ? a.data : {};
  const version = Number(a.version);
  return {
    meta: {
      id: a.id || actaId || null,
      asambleaId: a.asambleaId || null,
      buildingid: a.buildingid ?? null,
      status: a.status || "borrador",
      version: Number.isFinite(version) ? version : 0,
      actaNumero: a.actaNumero || null,
      origen: a.origen || "manual",
      fecha: a.fecha || data.fecha || "",
      tipo: a.tipo || data.tipo || "ordinaria",
      edificioNombre: a.edificioNombre || data.edificioNombre || "",
      padronSnapshot: a.padronSnapshot || null,
      resultadosSnapshot: a.resultadosSnapshot || null,
      publicadaEnMs: a.publicadaEnMs || null,
      envioActa: a.envioActa || null,
      updatedAtMs: a.updatedAtMs || null,
      // Acta firmada vigente (la que ven los propietarios): {version, subidaEnMs, subidaPor, bytes, anteriores} o null.
      firmada: res && res.firmada && typeof res.firmada === "object" ? res.firmada : null,
    },
    data,
    revision: (res && res.revision) || a.revision || null,
    ia: (res && res.ia) || null,
    revisiones: normalizarRevisiones(res && res.revisiones),
    puedePublicar: puedePublicarDe(res),
  };
}

function normalizarPunto(p, i) {
  const base = emptyPunto(i + 1);
  const votacion = (p && p.votacion) || {};
  return {
    ...base,
    ...p,
    votacion: {
      ...emptyVotacion(),
      ...votacion,
      votosPorPersona: { favor: [], contra: [], abstencion: [], ...(votacion.votosPorPersona || {}) },
    },
  };
}

/**
 * data guardada → acta que consumen ActaForm y ActaTemplate: defaults de
 * defaultActa, asistencia reconciliada con el padrón y, si está publicada, el
 * número que le asignó el servidor. En un acta sin publicar manda el N° de
 * `data` (el que se carga a mano en un edificio sin el módulo): la raíz de las
 * actas viejas del editor guarda el número que tenían antes, y guardar no la
 * actualiza.
 */
export function actaParaEditor(data, edificio, meta = {}) {
  const d = data || {};
  const base = defaultActa(edificio);
  const puntos = Array.isArray(d.ordenDelDia) ? d.ordenDelDia : base.ordenDelDia;
  const acta = {
    ...base,
    ...d,
    id: null,
    asistencia: reconciliarAsistencia(d.asistencia, edificio),
    ordenDelDia: puntos.map(normalizarPunto),
  };
  if (meta && meta.status === "publicada" && meta.actaNumero) acta.actaNumero = String(meta.actaNumero);
  return acta;
}

/** Acta nueva para una asamblea: tipo, fecha y orden del día con los ids de la asamblea. */
export function actaDesdeAsamblea(asamblea, edificio) {
  const base = defaultActa(edificio);
  if (!asamblea) return base;
  const tipo = asamblea.tipo === "extraordinaria" ? "extraordinaria" : "ordinaria";
  const puntos = (asamblea.ordenDelDia || [])
    .filter((p) => p && p.titulo)
    .map((p, i) => ({ ...emptyPunto(i + 1), id: String(p.id || `pt_${i + 1}`), titulo: String(p.titulo) }));
  return {
    ...base,
    tipo,
    tituloPortada: tipo === "extraordinaria" ? "Acta de Asamblea Extraordinaria" : base.tituloPortada,
    fecha: isoAUtc(asamblea.fecha) != null ? asamblea.fecha : base.fecha,
    ordenDelDia: puntos.length ? puntos : base.ordenDelDia,
  };
}

/** data lista para mandar a guardar/crear (JSON plano, con la forma de defaultActa). */
export function dataParaGuardar(acta) {
  return JSON.parse(JSON.stringify({ ...acta, id: null }));
}

/** El acta de una asamblea dentro de la lista de actas del edificio. */
export function actaDeAsamblea(actas, aid) {
  if (!aid) return null;
  return (actas || []).find((x) => x && (x.asambleaId === aid || x.id === `asm_${aid}`)) || null;
}

// ───────────────────────── Revisión (marcas y citas) ─────────────────────────

const CAMPOS = {
  asistencia: "Asistencia",
  fecha: "Fecha",
  horaApertura: "Hora de apertura",
  horaCierre: "Hora de cierre",
  convocatoria: "Convocatoria",
  tipo: "Tipo de asamblea",
  ciudad: "Ciudad",
  textoApertura: "Texto de apertura",
  textoCierre: "Texto de cierre",
  presidencia: "Presidencia",
  presidenciaId: "Presidencia",
  presidenciaNombre: "Presidencia",
  secretaria: "Secretaría",
  secretariaId: "Secretaría",
  secretariaNombre: "Secretaría",
  titulo: "Título",
  cuerpo: "Desarrollo",
  resumen: "Resumen",
  resultado: "Resultado",
  votos: "Votos",
  votacion: "Votación",
  tipoResolucion: "Tipo de resolución",
  textoResolucion: "Resolución",
  mociones: "Mociones",
  observaciones: "Observaciones",
  ordenDelDia: "Orden del día",
  // Campos de las marcas de la IA (CAMPOS_MARCA en propia_agent/asambleas/acta_ia.py).
  punto: "Punto",
  evidencias: "Citas del audio",
  general: "General",
  // Rutas de los cambios (minuta, deshacer).
  presente: "Presente",
  votanteId: "Votante",
  votanteNombre: "Apoderado",
  nota: "Nota",
  unidadId: "Unidad",
  numero: "Número",
  seccion: "Sección",
  metodo: "Método de votación",
  favor: "A favor",
  contra: "En contra",
  abstencion: "Abstención",
  actaNumero: "N° de acta",
  folio: "Folio",
  libro: "Libro",
  padron: "Padrón",
  edificioNombre: "Edificio",
  tituloPortada: "Título de la portada",
  administracion: "Administración",
  administracionDetalle: "Administración",
  presidenciaSub: "Presidencia",
  secretariaSub: "Secretaría",
  revision: "Revisión",
  marcas: "Marcas",
};

/** Nombre legible de un campo o ruta del acta ("ordenDelDia.p1.votacion" → "Orden del día › p1 › Votación"). */
export function etiquetaCampo(campo) {
  const partes = String(campo || "").split(".").filter(Boolean);
  if (!partes.length) return "";
  return partes.map((p) => CAMPOS[p] || p).join(" › ");
}

/**
 * Tokens de una ruta de cambio con la gramática del servidor: "data."
 * opcional, separadores "." o "/", y elementos de lista como "lista.2",
 * "lista[2]", "lista[id]" o "lista[clave=valor]".
 */
function tokensRuta(ruta) {
  const out = [];
  const re = /\[([^\]]*)\]|([^./[\]]+)/g;
  const s = String(ruta || "").trim();
  let m = re.exec(s);
  while (m) {
    if (m[1] != null) {
      const dentro = m[1].trim();
      const eq = dentro.indexOf("=");
      const limpiar = (x) => x.trim().replace(/^["']|["']$/g, "");
      if (/^\d{1,6}$/.test(dentro)) out.push({ indice: Number(dentro) });
      else if (eq > 0) out.push({ clave: dentro.slice(0, eq).trim(), valor: limpiar(dentro.slice(eq + 1)) });
      else if (dentro) out.push({ valor: limpiar(dentro) });
    } else if (/^\d{1,6}$/.test(m[2])) {
      out.push({ indice: Number(m[2]) });
    } else {
      out.push({ campo: m[2] });
    }
    m = re.exec(s);
  }
  if (out.length && out[0].campo === "data") out.shift();
  return out;
}

const CLAVES_ID = ["id", "unidadId", "puntoId"];

/** Posición de un elemento de lista según el token (índice, selector o id). */
function indiceEnLista(lista, tok) {
  if (tok.indice != null) return tok.indice;
  const claves = tok.clave ? [tok.clave] : CLAVES_ID;
  for (const k of claves) {
    const i = lista.findIndex((e) => e && typeof e === "object" && e[k] != null && String(e[k]) === tok.valor);
    if (i >= 0) return i;
  }
  return -1;
}

/** "Punto 03 · Título" / "Apto 101" para el elemento de una lista del acta, o null. */
function nombreDeElemento(lista, tok, acta, edificio) {
  const items = (acta && Array.isArray(acta[lista]) && acta[lista]) || [];
  const i = indiceEnLista(items, tok);
  if (lista === "ordenDelDia") {
    if (i >= 0 && items[i]) return `Punto ${pad2(i + 1)} · ${items[i].titulo || "sin título"}`;
    return tok.indice != null ? `Punto ${pad2(tok.indice + 1)}` : "Punto del orden del día";
  }
  const unidadId = i >= 0 && items[i] ? items[i].unidadId : tok.indice == null ? tok.valor : null;
  if (unidadId == null) return "Unidad";
  const u = ((edificio && edificio.unidades) || []).find((x) => String(x.id) === String(unidadId));
  return u ? nombreUnidad(u.etiqueta) : `Unidad ${unidadId}`;
}

/**
 * Nombre legible de la ruta de un cambio: "ordenDelDia.1.textoResolucion" →
 * "Punto 02 · Obras › Resolución"; "asistencia[unidadId=a1].presente" →
 * "Apto 101 › Presente". Con `soloCampo`, lo que va después del elemento
 * ("Resolución", "Presente"), para cuando el punto o la unidad ya se nombró.
 */
export function etiquetaRuta(ruta, acta, edificio, { soloCampo = false } = {}) {
  const toks = tokensRuta(ruta);
  const partes = [];
  let desde = 0;
  for (let k = 0; k < toks.length; k += 1) {
    const t = toks[k];
    const sig = toks[k + 1];
    if ((t.campo === "ordenDelDia" || t.campo === "asistencia") && sig && sig.campo == null) {
      partes.push(nombreDeElemento(t.campo, sig, acta, edificio));
      desde = partes.length;
      k += 1;
    } else if (t.campo === "votosPorPersona") {
      // "Votación › A favor" se lee mejor que "Votación › votosPorPersona › A favor".
    } else if (t.campo != null) {
      partes.push(CAMPOS[t.campo] || t.campo);
    } else {
      partes.push(t.indice != null ? String(t.indice + 1) : t.valor);
    }
  }
  const out = soloCampo && desde > 0 && desde < partes.length ? partes.slice(desde) : partes;
  return out.filter(Boolean).join(" › ");
}

export function marcasDe(revision) {
  return Array.isArray(revision && revision.marcas) ? revision.marcas.filter(Boolean) : [];
}

export const totalMarcas = (revision) => marcasDe(revision).length;

// Seguimiento de cada marca, guardado en la revisión del acta (va con "Guardar
// cambios"): estadoRevision "resuelta" | "no_resuelta" (tick verde / rojo) y
// cerrada (la cruz la saca de la lista; se vuelve a ver en el filtro "Cerradas").
export const ESTADOS_MARCA = ["resuelta", "no_resuelta"];
export const estadoDeMarca = (m) => (m && ESTADOS_MARCA.includes(m.estadoRevision) ? m.estadoRevision : null);
export const marcaCerrada = (m) => !!(m && m.cerrada === true);

/** Marcas que todavía piden atención: ni resueltas ni cerradas. */
export const marcasPendientes = (revision) =>
  marcasDe(revision).filter((m) => estadoDeMarca(m) !== "resuelta" && !marcaCerrada(m)).length;

/** Filtros del panel de revisión: qué marcas muestra cada uno. */
export const FILTROS_MARCAS = [
  { id: "abiertas", label: "Abiertas", incluye: (m) => !marcaCerrada(m) },
  { id: "pendientes", label: "Sin revisar", incluye: (m) => !marcaCerrada(m) && !estadoDeMarca(m) },
  { id: "resueltas", label: "Resueltas", incluye: (m) => estadoDeMarca(m) === "resuelta" },
  { id: "no_resueltas", label: "No resueltas", incluye: (m) => estadoDeMarca(m) === "no_resuelta" },
  { id: "cerradas", label: "Cerradas", incluye: (m) => marcaCerrada(m) },
];

/** La revisión con los cambios de seguimiento de la marca `indice` (índice en revision.marcas). */
export function conEstadoDeMarca(revision, indice, cambios) {
  const marcas = Array.isArray(revision && revision.marcas) ? revision.marcas : [];
  if (!marcas[indice]) return revision;
  const sig = { ...marcas[indice], ...cambios };
  if (!ESTADOS_MARCA.includes(sig.estadoRevision)) delete sig.estadoRevision;
  if (sig.cerrada !== true) delete sig.cerrada;
  return { ...revision, marcas: marcas.map((m, i) => (i === indice ? sig : m)) };
}

/** Marcas agrupadas por punto del orden del día (puntoId → marcas). */
export function marcasPorPunto(revision) {
  const m = new Map();
  for (const x of marcasDe(revision)) {
    if (x.puntoId == null || x.puntoId === "") continue;
    const k = String(x.puntoId);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(x);
  }
  return m;
}

/** Marcas que no son de un punto (asistencia, horas, firmas…). */
export const marcasGenerales = (revision) => marcasDe(revision).filter((x) => x.puntoId == null || x.puntoId === "");

/** Citas de un punto: las de sus marcas y las que dejó la IA, sin repetir segmento, por tiempo. */
export function evidenciasDePunto(puntoId, revision, ia) {
  const vistas = new Map();
  const sumar = (ev) => {
    if (!ev || ev.cita == null) return;
    const k = ev.segmentId != null ? `s${ev.segmentId}` : `t${ev.inicioMs}:${ev.cita}`;
    if (!vistas.has(k)) vistas.set(k, ev);
  };
  (marcasPorPunto(revision).get(String(puntoId)) || []).forEach((mk) => (mk.evidencias || []).forEach(sumar));
  const porPunto = (ia && ia.evidenciasPorPunto) || {};
  (porPunto[puntoId] || []).forEach(sumar);
  return [...vistas.values()].sort((a, b) => (Number(a.inicioMs) || 0) - (Number(b.inicioMs) || 0));
}

// ───────────────────────── Publicación ─────────────────────────

/**
 * Chequeo antes de "Aprobar y publicar": `bloqueos` impiden publicar,
 * `avisos` se muestran para que la mesa los confirme.
 */
export function revisarParaPublicar(acta, edificio, revision) {
  const bloqueos = [];
  const avisos = [];
  const a = acta || {};
  if (isoAUtc(a.fecha) == null) bloqueos.push("Falta la fecha de la asamblea.");
  const puntos = a.ordenDelDia || [];
  if (!puntos.length) bloqueos.push("El orden del día no tiene puntos.");
  puntos.forEach((p, i) => {
    if (!String((p && p.titulo) || "").trim()) bloqueos.push(`El punto ${i + 1} no tiene título.`);
    if (p && p.tipoResolucion !== "informativa" && !validarVotacion(p.votacion).valido) {
      bloqueos.push(`En el punto ${i + 1} hay quien votó en dos sentidos a la vez.`);
    }
  });
  if (!(a.asistencia || []).some((r) => r.presente)) avisos.push("No hay unidades marcadas como presentes.");
  else {
    const q = calcularQuorum(a, edificio, umbralQuorum(a.convocatoria));
    if (!q.habilitante) {
      avisos.push(`El quórum presente (${q.coeficientePresente} %) no alcanza el 50 % que pide la primera convocatoria.`);
    }
  }
  if (!a.horaApertura || !a.horaCierre) avisos.push("Falta la hora de apertura o la de cierre.");
  if (!a.presidenciaId && !a.presidenciaNombre) avisos.push("No hay nadie en la presidencia para firmar.");
  if (!a.secretariaId && !a.secretariaNombre) avisos.push("No hay nadie en la secretaría para firmar.");
  const total = totalMarcas(revision);
  const marcas = marcasPendientes(revision);
  if (marcas && marcas === total) avisos.push(`La IA dejó ${marcas} ${marcas === 1 ? "marca" : "marcas"} para revisar.`);
  else if (marcas) avisos.push(`${marcas === 1 ? "Queda 1" : `Quedan ${marcas}`} de ${total} marcas de la IA sin resolver.`);
  return { bloqueos, avisos, marcas };
}

// ───────────────────────── Cambios de una minuta ─────────────────────────

const OPS_MINUTA = {
  set_campo: "Dato del acta",
  set_asistencia: "Asistencia",
  set_texto_punto: "Texto del punto",
  set_resultado: "Resultado",
  set_votos: "Votos",
  agregar_punto: "Punto agregado",
  quitar_punto: "Punto quitado",
  set_firma: "Firma",
};

const truncar = (s, n = 160) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const largo = (x) => (Array.isArray(x) ? x.length : 0);
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// Qué cuenta una lista según el campo que cambió ("ordenDelDia": 5 → 6 puntos).
const CONTEO_LISTA = {
  ordenDelDia: ["punto", "puntos"],
  asistencia: ["unidad", "unidades"],
  favor: ["persona", "personas"],
  contra: ["persona", "personas"],
  abstencion: ["persona", "personas"],
  mociones: ["moción", "mociones"],
  observaciones: ["observación", "observaciones"],
};
const CAMPOS_PERSONA = new Set(["votanteId", "presidenciaId", "secretariaId"]);
const CAMPOS_VOTOS = new Set(["favor", "contra", "abstencion"]);

/** Último campo con nombre de una ruta ("ordenDelDia.1.votacion.votosPorPersona.favor" → "favor"). */
function campoFinal(ruta) {
  const toks = tokensRuta(ruta);
  for (let k = toks.length - 1; k >= 0; k -= 1) if (toks[k].campo) return toks[k].campo;
  return "";
}

/**
 * Valor de un cambio en una línea. `campo` da contexto (ids de personas →
 * nombres, listas → "3 personas") y `nombres` es el Map id → nombre del padrón.
 */
function resumirValor(v, campo = "", nombres = null) {
  if (v === undefined) return null;
  if (v === null || v === "") return "—";
  const nombre = (id) => (nombres && nombres.get(String(id))) || String(id);
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if (CAMPOS_PERSONA.has(campo) && (typeof v === "string" || typeof v === "number")) return truncar(nombre(v));
  if (typeof v === "string" || typeof v === "number") return truncar(String(v));
  if (Array.isArray(v)) {
    if (CAMPOS_VOTOS.has(campo) && nombres && v.every((x) => typeof x === "string" || typeof x === "number")) {
      if (!v.length) return "nadie";
      const primeros = v.slice(0, 3).map(nombre).join(", ");
      return truncar(v.length > 3 ? `${primeros} y ${v.length - 3} más` : primeros);
    }
    const [uno, varios] = CONTEO_LISTA[campo] || ["elemento", "elementos"];
    return plural(v.length, uno, varios);
  }
  if (typeof v === "object") {
    if ("favor" in v || "contra" in v || "abstencion" in v) {
      return `A favor ${largo(v.favor)} · En contra ${largo(v.contra)} · Abstención ${largo(v.abstencion)}`;
    }
    if ("presente" in v) return v.presente ? "Presente" : "Ausente";
    if (typeof v.titulo === "string") return truncar(v.titulo);
    if (typeof v.nombre === "string") return truncar(v.nombre);
    return truncar(JSON.stringify(v));
  }
  return truncar(String(v));
}

const mayuscula = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Un cambio de minuta → { titulo, donde, antes, despues, detalles, cita,
 * confianza } para mostrar. Acepta:
 *  - la operación aplicada que guarda el worker: {op, descripcion, rutas, cita, confianza};
 *  - un cambio de revisión: {op, ruta, antes, despues};
 *  - la forma {op, puntoId|unidadId, valor, cita, confianza}.
 * `propios` son los cambios de la revisión ({ruta, antes, despues}) que hizo
 * esta operación: con uno solo se muestra antes → después; con varios, uno por
 * línea en `detalles`. `copropietarios` sirve para nombrar a las personas.
 */
export function describirCambio(cambio, acta, edificio, propios = null, copropietarios = null) {
  const c = cambio || {};
  const puntos = (acta && acta.ordenDelDia) || [];
  const nombres = Array.isArray(copropietarios) ? new Map(copropietarios.map((p) => [String(p.id), p.nombre])) : null;
  const rutas = Array.isArray(c.rutas) ? c.rutas.filter((r) => typeof r === "string" && r) : [];
  let donde = "";
  if (typeof c.descripcion === "string" && c.descripcion.trim()) {
    donde = mayuscula(truncar(c.descripcion.trim()));
  } else if (c.puntoId != null && c.puntoId !== "") {
    const i = puntos.findIndex((p) => String(p.id) === String(c.puntoId));
    donde = i >= 0 ? `Punto ${pad2(i + 1)} · ${puntos[i].titulo || "sin título"}` : "Punto del orden del día";
  } else if (c.unidadId != null && c.unidadId !== "") {
    const u = ((edificio && edificio.unidades) || []).find((x) => String(x.id) === String(c.unidadId));
    donde = u ? nombreUnidad(u.etiqueta) : `Unidad ${c.unidadId}`;
  } else if (c.ruta) {
    donde = etiquetaRuta(c.ruta, acta, edificio);
  } else if (rutas.length) {
    donde = etiquetaRuta(rutas[0], acta, edificio);
  } else if (c.campo) {
    donde = etiquetaCampo(c.campo);
  }

  const campo = campoFinal(c.ruta || "");
  let antes = c.omitido ? null : resumirValor(c.antes, campo, nombres);
  let despues = c.omitido ? null : resumirValor(c.despues !== undefined ? c.despues : c.valor, campo, nombres);
  let detalles = [];
  const lista = (Array.isArray(propios) ? propios : []).filter((x) => x && !x.omitido);
  if (antes == null && despues == null && lista.length === 1) {
    const f = campoFinal(lista[0].ruta);
    antes = resumirValor(lista[0].antes, f, nombres);
    despues = resumirValor(lista[0].despues, f, nombres);
  } else if (antes == null && despues == null && lista.length > 1) {
    detalles = lista.map((x) => {
      const f = campoFinal(x.ruta);
      return {
        campo: etiquetaRuta(x.ruta, acta, edificio, { soloCampo: true }),
        antes: resumirValor(x.antes, f, nombres),
        despues: resumirValor(x.despues, f, nombres),
      };
    });
  }
  return {
    titulo: OPS_MINUTA[c.op] || "Cambio",
    donde,
    antes,
    despues,
    detalles,
    cita: c.cita ? String(c.cita) : null,
    confianza: c.confianza || null,
  };
}

/**
 * Lista de cambios de una minuta aplicada para mostrar antes de "Deshacer".
 * Cada operación aplicada ({descripcion, rutas, …}) se junta con sus cambios
 * de la revisión (`minuta.cambios`, que estadoMinuta devuelve ya aplicada):
 * a cada ruta le toca el primer cambio todavía libre con esa ruta (el worker
 * los guarda en el mismo orden). Si solo vinieron los cambios, se muestran esos.
 */
export function cambiosDeMinuta(minuta, acta, edificio, copropietarios = null) {
  const m = minuta || {};
  const aplicadas = Array.isArray(m.aplicadas) ? m.aplicadas.filter(Boolean) : [];
  const cambios = Array.isArray(m.cambios) ? m.cambios.filter((x) => x && typeof x === "object") : [];
  if (!aplicadas.length) return cambios.map((x) => describirCambio(x, acta, edificio, null, copropietarios));
  const usados = new Set();
  return aplicadas.map((a) => {
    const rutas = Array.isArray(a.rutas) ? a.rutas : [];
    const propios = [];
    for (const r of rutas) {
      const k = cambios.findIndex((x, i) => !usados.has(i) && x.ruta === r);
      if (k >= 0) {
        usados.add(k);
        propios.push(cambios[k]);
      }
    }
    return describirCambio(a, acta, edificio, propios, copropietarios);
  });
}

/** Largo máximo de la minuta escrita (functions: LIMITES.minutaTextoChars). */
export const MAX_MINUTA_TEXTO = 8000;

// ───────────────────────── Audio ─────────────────────────

export const LIMITES_AUDIO = {
  bytesPorArchivo: 2 * 1024 ** 3,
  archivosPorAsamblea: 12,
  bytesMinuta: 100 * 1024 ** 2,
};

// Content-Type canónico por extensión: el navegador reporta variantes
// ("audio/x-m4a", "audio/x-wav" o vacío) y el PUT firmado tiene que llevar
// exactamente el tipo que se declaró al pedir la URL.
const MIME_POR_EXT = {
  webm: "audio/webm",
  ogg: "audio/ogg",
  opus: "audio/ogg",
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  mp4: "video/mp4",
  aac: "audio/aac",
  wav: "audio/wav",
};

export const EXTENSIONES_AUDIO = Object.keys(MIME_POR_EXT);

export const ACCEPT_AUDIO = EXTENSIONES_AUDIO.map((e) => `.${e}`).join(",");

export function extDe(nombre) {
  const m = /\.([a-z0-9]{1,5})$/i.exec(String(nombre || ""));
  return m ? m[1].toLowerCase() : "";
}

/** Content-Type a declarar para un archivo de audio, o null si la extensión no se admite. */
export function contentTypeDeArchivo(file) {
  return MIME_POR_EXT[extDe(file && file.name)] || null;
}

export const mimeBase = (m) => String(m || "").split(";")[0].trim().toLowerCase();

const EXT_POR_MIME = {
  "audio/webm": "webm",
  "audio/ogg": "ogg",
  "audio/mp4": "m4a",
  "audio/mpeg": "mp3",
  "audio/aac": "aac",
  "audio/wav": "wav",
  "video/mp4": "mp4",
};

export const extDeMime = (m) => EXT_POR_MIME[mimeBase(m)] || "";

// Orden de preferencia para grabar la minuta: Opus en WebM (Chrome, Firefox,
// Edge) y AAC en MP4 (Safari).
export const MIMES_GRABACION = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4", "audio/webm"];

export function elegirMimeGrabacion(MediaRecorderImpl) {
  if (!MediaRecorderImpl || typeof MediaRecorderImpl.isTypeSupported !== "function") return "";
  return (
    MIMES_GRABACION.find((m) => {
      try {
        return MediaRecorderImpl.isTypeSupported(m);
      } catch (_) {
        return false;
      }
    }) || ""
  );
}
